import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: linear-gradient(135deg, #0f1419 0%, #1a1f2e 100%);
  min-height: 100vh;
  color: #e2e8f0;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
  padding: 30px;
  background: rgba(247, 147, 26, 0.1);
  border-radius: 15px;
  border: 2px solid #f7931a;
`;

const Title = styled.h1`
  color: #f7931a;
  margin: 0 0 10px 0;
  font-size: 2.5rem;
  font-weight: bold;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #a0aec0;
  font-size: 1.1rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const StatCard = styled.div<{ color?: string }>`
  background: rgba(45, 55, 72, 0.8);
  padding: 25px;
  border-radius: 12px;
  border-left: 4px solid ${props => props.color || '#f7931a'};
  backdrop-filter: blur(10px);
`;

const StatNumber = styled.div<{ color?: string }>`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${props => props.color || '#f7931a'};
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: #a0aec0;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.div`
  background: rgba(45, 55, 72, 0.6);
  padding: 30px;
  border-radius: 15px;
  margin-bottom: 30px;
  backdrop-filter: blur(5px);
`;

const SectionTitle = styled.h2`
  color: #3182ce;
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TweetCard = styled.div<{ isBitcoin?: boolean }>`
  background: rgba(26, 32, 44, 0.8);
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 15px;
  border-left: 3px solid ${props => props.isBitcoin ? '#f7931a' : '#4a5568'};
`;

const TweetText = styled.div`
  margin-bottom: 10px;
  line-height: 1.5;
  font-size: 0.95rem;
`;

const TweetMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  color: #a0aec0;
`;

const EngagementStats = styled.div`
  display: flex;
  gap: 15px;
`;

const LoginPrompt = styled.div`
  text-align: center;
  padding: 60px 40px;
  background: rgba(45, 55, 72, 0.8);
  border-radius: 15px;
  backdrop-filter: blur(10px);
`;

const LoginInput = styled.input`
  padding: 15px 20px;
  font-size: 1.1rem;
  border: 2px solid #4a5568;
  border-radius: 8px;
  background: rgba(26, 32, 44, 0.8);
  color: #e2e8f0;
  margin: 0 10px;
  width: 300px;
  
  &:focus {
    outline: none;
    border-color: #f7931a;
  }
`;

const Button = styled.button`
  padding: 15px 30px;
  font-size: 1.1rem;
  background: #f7931a;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
  
  &:hover {
    background: #e6820a;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RefreshButton = styled(Button)`
  background: #3182ce;
  font-size: 0.9rem;
  padding: 10px 20px;
  
  &:hover {
    background: #2c5aa0;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #4a5568;
  border-radius: 50%;
  border-top-color: #f7931a;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const UserAnalyticsDashboard = () => {
  const [username, setUsername] = useState('');
  const [analytics, setAnalytics] = useState<any>(null);
  const [userStatus, setUserStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkUserStatus = async (user) => {
    try {
      const response = await fetch(`/api/user/${user}/status`);
      const data = await response.json();
      
      if (data.success) {
        setUserStatus(data);
        return data.hasTweets;
      }
      return false;
    } catch (err) {
      console.error('Status check failed:', err);
      return false;
    }
  };

  const loadAnalytics = async (user) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/user/${user}/analytics`);
      const data = await response.json();
      
      if (data.success) {
        setAnalytics(data);
      } else {
        throw new Error(data.error || 'Failed to load analytics');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!username.trim()) return;
    
    const cleanUsername = username.replace('@', '').toLowerCase();
    setUsername(cleanUsername);
    
    const hasData = await checkUserStatus(cleanUsername);
    
    if (hasData) {
      await loadAnalytics(cleanUsername);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    
    try {
      const response = await fetch(`/api/user/${username}/refresh`, { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        // Wait a bit then reload analytics
        setTimeout(() => {
          loadAnalytics(username);
          checkUserStatus(username);
        }, 2000);
      }
    } catch (err) {
      setError('Refresh failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setRefreshing(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num?.toLocaleString() || '0';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!analytics && !userStatus) {
    return (
      <DashboardContainer>
        <Header>
          <Title>🐦 Twitter Analytics Dashboard</Title>
          <Subtitle>Enter your X handle to see your Bitcoin tweet analytics</Subtitle>
        </Header>
        
        <LoginPrompt>
          <h2 style={{ color: '#3182ce', marginBottom: '20px' }}>Welcome! 👋</h2>
          <p style={{ marginBottom: '30px', color: '#a0aec0' }}>
            Get detailed analytics on your Bitcoin-related tweets, engagement patterns, and more.
          </p>
          
          <div>
            <LoginInput
              type="text"
              placeholder="Enter X handle (e.g., ejuline)"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            />
            <Button onClick={handleLogin} disabled={loading}>
              {loading ? <LoadingSpinner /> : 'Analyze Tweets'}
            </Button>
          </div>
          
          {error && (
            <div style={{ marginTop: '20px', color: '#ff6b6b' }}>
              Error: {error}
            </div>
          )}
        </LoginPrompt>
      </DashboardContainer>
    );
  }

  if (userStatus && !userStatus.hasTweets) {
    return (
      <DashboardContainer>
        <Header>
          <Title>🐦 @{username} Analytics</Title>
          <Subtitle>No data found - Click refresh to download tweets</Subtitle>
        </Header>
        
        <Section>
          <h2 style={{ textAlign: 'center', color: '#f7931a' }}>First Time Setup</h2>
          <p style={{ textAlign: 'center', marginBottom: '30px' }}>
            We haven't analyzed your tweets yet. Click below to download and analyze your complete Twitter history.
          </p>
          <div style={{ textAlign: 'center' }}>
            <Button onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? '📥 Downloading Tweets...' : '🚀 Start Analysis'}
            </Button>
          </div>
        </Section>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <Title>🐦 @{analytics?.username || username} Analytics</Title>
        <Subtitle>
          Bitcoin Tweet Dashboard • Last updated: {userStatus?.lastDownload ? formatDate(userStatus.lastDownload) : 'Unknown'}
          <RefreshButton onClick={handleRefresh} disabled={refreshing} style={{ marginLeft: '20px' }}>
            {refreshing ? '🔄 Updating...' : '🔄 Refresh Data'}
          </RefreshButton>
        </Subtitle>
      </Header>

      {/* Duplicate Prevention Stats */}
      <Section>
        <SectionTitle>🛡️ Data Protection Status</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div style={{ textAlign: 'center', padding: '15px', background: 'rgba(76, 175, 80, 0.2)', borderRadius: '8px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4CAF50' }}>
              {analytics?.duplicateInfo?.new_tweets || 0}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#a0aec0' }}>NEW TWEETS</div>
          </div>
          <div style={{ textAlign: 'center', padding: '15px', background: 'rgba(33, 150, 243, 0.2)', borderRadius: '8px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2196F3' }}>
              {analytics?.duplicateInfo?.updated_tweets || 0}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#a0aec0' }}>UPDATED (NO DUPLICATES)</div>
          </div>
          <div style={{ textAlign: 'center', padding: '15px', background: 'rgba(156, 39, 176, 0.2)', borderRadius: '8px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#9C27B0' }}>
              {analytics?.duplicateInfo?.download_sessions || 0}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#a0aec0' }}>DOWNLOAD SESSIONS</div>
          </div>
        </div>
      </Section>

      <StatsGrid>
        <StatCard color="#f7931a">
          <StatNumber color="#f7931a">{formatNumber(analytics?.stats?.total_tweets)}</StatNumber>
          <StatLabel>Total Tweets</StatLabel>
        </StatCard>
        
        <StatCard color="#4CAF50">
          <StatNumber color="#4CAF50">{formatNumber(analytics?.stats?.bitcoin_tweets)}</StatNumber>
          <StatLabel>Bitcoin Tweets ({Math.round((analytics?.stats?.bitcoin_tweets / analytics?.stats?.total_tweets) * 100)}%)</StatLabel>
        </StatCard>
        
        <StatCard color="#2196F3">
          <StatNumber color="#2196F3">{formatNumber(analytics?.stats?.avg_likes)}</StatNumber>
          <StatLabel>Avg Likes</StatLabel>
        </StatCard>
        
        <StatCard color="#FF6B6B">
          <StatNumber color="#FF6B6B">{formatNumber(analytics?.stats?.max_likes)}</StatNumber>
          <StatLabel>Best Tweet</StatLabel>
        </StatCard>
        
        <StatCard color="#9C27B0">
          <StatNumber color="#9C27B0">{analytics?.stats?.bullish_tweets}</StatNumber>
          <StatLabel>Bullish Tweets</StatLabel>
        </StatCard>
        
        <StatCard color="#FF9800">
          <StatNumber color="#FF9800">{analytics?.stats?.saylor_mentions}</StatNumber>
          <StatLabel>Saylor Mentions</StatLabel>
        </StatCard>
      </StatsGrid>

      <Section>
        <SectionTitle>🏆 Top Performing Tweets</SectionTitle>
        {analytics?.topTweets?.map((tweet, index) => (
          <TweetCard key={index} isBitcoin={tweet.is_bitcoin_related}>
            <TweetText>"{tweet.tweet_text}"</TweetText>
            <TweetMeta>
              <EngagementStats>
                <span>❤️ {formatNumber(tweet.like_count)}</span>
                <span>🔄 {formatNumber(tweet.retweet_count)}</span>
                <span>{tweet.is_bitcoin_related ? '₿' : '📝'}</span>
              </EngagementStats>
              <span>{formatDate(tweet.tweet_created_at)}</span>
            </TweetMeta>
          </TweetCard>
        ))}
      </Section>

      <Section>
        <SectionTitle>📊 Account Summary</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <strong>📅 Account Activity</strong>
            <p>First tweet: {formatDate(analytics?.stats?.first_tweet)}</p>
            <p>Latest tweet: {formatDate(analytics?.stats?.latest_tweet)}</p>
            <p>Total engagement: {formatNumber(analytics?.stats?.total_likes)} likes</p>
          </div>
          <div>
            <strong>₿ Bitcoin Focus</strong>
            <p>Bitcoin tweets: {analytics?.stats?.bitcoin_tweets}</p>
            <p>Bullish sentiment: {analytics?.stats?.bullish_tweets}</p>
            <p>Bearish sentiment: {analytics?.stats?.bearish_tweets}</p>
          </div>
          <div>
            <strong>🎯 Engagement</strong>
            <p>Average likes: {analytics?.stats?.avg_likes}</p>
            <p>Average retweets: {analytics?.stats?.avg_retweets}</p>
            <p>Best performing: {formatNumber(analytics?.stats?.max_likes)} likes</p>
          </div>
        </div>
      </Section>
    </DashboardContainer>
  );
};

export default UserAnalyticsDashboard;